import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { API_BASE_URL } from '../config';
import logo from '../assets/images/candid-i-logo.png';
import {
  clampText,
  formatMarketDate,
  handleMarketImageError,
  normalizeMarketIntelligenceItem,
} from './marketIntelligenceUtils';

const SECTION_COPY = {
  archive: {
    eyebrow: 'Archive',
    title: 'Browse the full intelligence library',
    description: 'Search by sector, region, topic, author, and content type.',
  },
  search: {
    eyebrow: 'Search',
    title: 'Search intelligence by keyword',
    description: 'Find reports, market briefs, and editorial notes quickly.',
  },
  subscribe: {
    eyebrow: 'Subscribe',
    title: 'Receive market intelligence by email',
    description: 'Join the audience for new reports, movement signals, and briefings.',
  },
  briefing: {
    eyebrow: 'Briefing',
    title: 'Join the market briefing',
    description: 'Tell us the market, company, or talent segment you want us to unpack.',
  },
  filter: {
    eyebrow: 'Filtered view',
    title: 'Curated market coverage',
    description: 'Browse content filtered by a specific taxonomy value.',
  },
};

const normalizeParam = (value) => {
  if (!value) return '';
  return decodeURIComponent(String(value)).replace(/-/g, ' ').trim().toLowerCase();
};

const MarketIntelligenceSection = ({ mode = 'archive', filterKey = null }) => {
  const navigate = useNavigate();
  const { value } = useParams();
  const location = useLocation();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [subscribeForm, setSubscribeForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    audienceType: 'company',
    preferences: '',
    consent: true,
  });
  const [subscribeSuccess, setSubscribeSuccess] = useState(false);
  const [briefingForm, setBriefingForm] = useState({ name: '', email: '', company: '', message: '' });
  const copy = SECTION_COPY[mode] || SECTION_COPY.archive;

  useEffect(() => {
    fetchArticles();
  }, []);

  useEffect(() => {
    if (mode === 'search') {
      const params = new URLSearchParams(location.search);
      const q = params.get('q') || '';
      setSearchInput(q);
      setSubmittedSearch(q);
    }
  }, [location.search, mode]);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/market-intelligence/articles`, { params: { page: 1, per_page: 100 } });
      const apiArticles = response.data?.data || [];
      const normalized = apiArticles.map(normalizeMarketIntelligenceItem);
      setArticles(normalized);
    } catch (error) {
      console.error('Error fetching market intelligence section data:', error);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredArticles = useMemo(() => {
    let results = [...articles];

    if (mode === 'filter' && filterKey && value) {
      const normalizedValue = normalizeParam(value);
      results = results.filter((article) => {
        const source = {
          category: normalizeParam(article.category),
          topic: normalizeParam((article.topics || []).join(', ')),
          sector: normalizeParam((article.sector || []).join(', ')),
          region: normalizeParam((article.region || []).join(', ')),
          author: normalizeParam(article.author_name),
          practice: normalizeParam(article.raw?.practice_id || article.raw?.practice_name || ''),
          vertical: normalizeParam((article.raw?.vertical_ids || []).join(', ')),
          function: normalizeParam((article.raw?.function_ids || []).join(', ')),
          taxonomy_topic: normalizeParam((article.raw?.subcategory_ids || []).join(', ')),
        };

        if (filterKey === 'topic') {
          return (
            source.topic.includes(normalizedValue) ||
            source.taxonomy_topic.includes(normalizedValue)
          );
        }
        return source[filterKey] && source[filterKey].includes(normalizedValue);
      });
    }

    const q = (mode === 'search' ? submittedSearch : '').trim().toLowerCase();
    if (q) {
      results = results.filter((article) =>
        article.title.toLowerCase().includes(q) ||
        article.excerpt.toLowerCase().includes(q) ||
        article.subtitle.toLowerCase().includes(q) ||
        (article.tags || []).some((tag) => tag.toLowerCase().includes(q)) ||
        (article.topics || []).some((topic) => topic.toLowerCase().includes(q))
      );
    }

    return results.sort((a, b) => new Date(b.published_at) - new Date(a.published_at));
  }, [articles, mode, filterKey, value, submittedSearch]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSubmittedSearch(searchInput);
    const params = new URLSearchParams(location.search);
    if (searchInput) {
      params.set('q', searchInput);
    } else {
      params.delete('q');
    }
    navigate({ pathname: '/market-intelligence/search', search: params.toString() ? `?${params.toString()}` : '' });
  };

  const handleSubscribeSubmit = (event) => {
    event.preventDefault();
    setSubscribeSuccess(true);
  };

  const handleBriefingSubmit = (event) => {
    event.preventDefault();
    alert('Briefing request placeholder saved for next pass.');
  };

  return (
    <div className="min-h-screen bg-[#f7f7f4] text-[#0f172a]">
      <header className="sticky top-0 z-40 border-b border-black/5 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[88rem] items-center justify-between px-5 py-4 lg:px-8">
          <button onClick={() => navigate('/')} className="flex items-center gap-3">
            <img src={logo} alt="Candid-I" className="h-8 w-auto" />
          </button>
          <div className="flex items-center gap-3 text-sm font-medium">
            <Link to="/market-intelligence" className="transition hover:text-[#f7911d]">
              Hub
            </Link>
            <Link to="/market-intelligence/archive" className="transition hover:text-[#f7911d]">
              Archive
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[88rem] px-5 py-12 lg:px-8">
        <section className="rounded-[2.5rem] bg-white p-8 shadow-sm lg:p-10">
          <div className="text-xs font-semibold uppercase tracking-[0.35em] text-[#f7911d]">
            {copy.eyebrow}
          </div>
          <h1 className="mt-4 text-4xl font-semibold tracking-tight text-slate-950 md:text-5xl">
            {copy.title}
          </h1>
          <p className="mt-4 max-w-3xl text-lg leading-8 text-slate-600">
            {copy.description}
          </p>

          {mode === 'search' && (
            <form onSubmit={handleSearchSubmit} className="mt-8 flex flex-col gap-3 md:flex-row">
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by keyword, sector, author, or topic"
                className="flex-1 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-[#f7911d]"
              />
              <button className="rounded-2xl bg-[#f7911d] px-6 py-3 text-sm font-semibold text-white">
                Search
              </button>
            </form>
          )}
        </section>

        {mode === 'subscribe' && (
          <section className="mt-8 space-y-6">
            <section className="rounded-[2rem] bg-white p-8 shadow-sm lg:p-10">
              <div className="text-xs font-semibold uppercase tracking-[0.35em] text-[#f7911d]">
                Subscription
              </div>
              <h2 className="mt-4 max-w-4xl text-4xl font-semibold tracking-tight text-slate-950 md:text-5xl">
                Stay close to the market.
              </h2>
              <p className="mt-4 max-w-3xl text-lg leading-8 text-slate-600">
                Subscribe for market notes, talent movement signals, briefing invites, and editorial updates
                curated for employers and candidates who want the market in real time.
              </p>
            </section>

            <section className="grid gap-6 lg:grid-cols-[0.92fr_1.08fr]">
              <div className="rounded-[2rem] bg-slate-950 p-8 text-white shadow-sm">
                <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#f7911d]">
                  What you’ll receive
                </div>
                <h3 className="mt-4 text-3xl font-semibold">Relevant updates, not inbox noise.</h3>
                <p className="mt-4 text-sm leading-7 text-slate-300">
                  This subscription is designed to stay editorial, concise, and useful. We’ll only share the
                  signals that matter for search, hiring, and market mapping.
                </p>

                <div className="mt-8 grid gap-3">
                  {[
                    'Market notes across sectors, regions, and functions',
                    'Talent movement signals and hiring pattern observations',
                    'Briefing invites for employers and candidates',
                    'Short video clips sized for fast executive review',
                  ].map((item) => (
                    <div key={item} className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-200">
                      {item}
                    </div>
                  ))}
                </div>

                <div className="mt-8 rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="text-xs font-semibold uppercase tracking-[0.28em] text-[#f7911d]">
                    Editorial standard
                  </div>
                  <p className="mt-3 text-sm leading-7 text-slate-300">
                    We keep video clips between 90 and 120 seconds and write every update so it reads well on desktop,
                    mobile, and email.
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubscribeSubmit} className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm">
                <div className="grid gap-4 md:grid-cols-2">
                  <input
                    required
                    value={subscribeForm.firstName}
                    onChange={(e) => setSubscribeForm((prev) => ({ ...prev, firstName: e.target.value }))}
                    placeholder="First name"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-[#f7911d]"
                  />
                  <input
                    required
                    value={subscribeForm.lastName}
                    onChange={(e) => setSubscribeForm((prev) => ({ ...prev, lastName: e.target.value }))}
                    placeholder="Last name"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-[#f7911d]"
                  />
                </div>

                <div className="mt-4 grid gap-4">
                  <input
                    required
                    type="email"
                    value={subscribeForm.email}
                    onChange={(e) => setSubscribeForm((prev) => ({ ...prev, email: e.target.value }))}
                    placeholder="Email address"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-[#f7911d]"
                  />
                  <input
                    value={subscribeForm.company}
                    onChange={(e) => setSubscribeForm((prev) => ({ ...prev, company: e.target.value }))}
                    placeholder="Company or current organisation"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-[#f7911d]"
                  />
                  <select
                    value={subscribeForm.audienceType}
                    onChange={(e) => setSubscribeForm((prev) => ({ ...prev, audienceType: e.target.value }))}
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-[#f7911d]"
                  >
                    <option value="company">I am representing a company</option>
                    <option value="candidate">I want career updates</option>
                    <option value="journalist">I’m a journalist or researcher</option>
                    <option value="other">Other market signal updates</option>
                  </select>
                  <input
                    value={subscribeForm.preferences}
                    onChange={(e) => setSubscribeForm((prev) => ({ ...prev, preferences: e.target.value }))}
                    placeholder="Preferences, e.g. precious metals, Singapore, compensation"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-[#f7911d]"
                  />
                </div>

                <label className="mt-5 flex items-start gap-3 rounded-2xl bg-slate-50 p-4 text-sm text-slate-600">
                  <input
                    type="checkbox"
                    checked={subscribeForm.consent}
                    onChange={(e) => setSubscribeForm((prev) => ({ ...prev, consent: e.target.checked }))}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-[#f7911d] focus:ring-[#f7911d]"
                  />
                  <span>
                    I agree to receive editorial updates and related communications from ALICE / Candid-I.
                  </span>
                </label>

                <button className="mt-5 w-full rounded-2xl bg-[#f7911d] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#e77e08]">
                  Subscribe
                </button>

                {subscribeSuccess && (
                  <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                    Thanks - your subscription request has been captured.
                  </div>
                )}
              </form>
            </section>
          </section>
        )}

        {mode === 'briefing' && (
          <section className="mt-8 grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
            <div className="rounded-[2rem] bg-[#0f172a] p-8 text-white shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#f7911d]">
                Briefing request
              </div>
              <h2 className="mt-4 text-3xl font-semibold">Request a tailored market note.</h2>
              <p className="mt-4 text-sm leading-7 text-slate-300">
                We will capture the scope, the market, and the signal you want. Keep it tight and commercially useful.
              </p>
            </div>
            <form onSubmit={handleBriefingSubmit} className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm">
              <div className="grid gap-4">
                <input
                  value={briefingForm.name}
                  onChange={(e) => setBriefingForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Name"
                  className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-[#f7911d]"
                />
                <input
                  value={briefingForm.email}
                  onChange={(e) => setBriefingForm((prev) => ({ ...prev, email: e.target.value }))}
                  placeholder="Email"
                  className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-[#f7911d]"
                />
                <input
                  value={briefingForm.company}
                  onChange={(e) => setBriefingForm((prev) => ({ ...prev, company: e.target.value }))}
                  placeholder="Company"
                  className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-[#f7911d]"
                />
                <textarea
                  rows={5}
                  value={briefingForm.message}
                  onChange={(e) => setBriefingForm((prev) => ({ ...prev, message: e.target.value }))}
                  placeholder="What market or talent segment should we analyse?"
                  className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-[#f7911d]"
                />
                <button className="rounded-2xl bg-[#f7911d] px-6 py-3 text-sm font-semibold text-white">
                  Join the market briefing
                </button>
              </div>
            </form>
          </section>
        )}

        {(mode === 'archive' || mode === 'search' || mode === 'filter') && (
          <section className="mt-8">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-2xl font-semibold text-slate-950">
                {filteredArticles.length} insight{filteredArticles.length === 1 ? '' : 's'}
              </h2>
              {mode === 'filter' && value && (
                <div className="text-sm text-slate-500">
                  Filtered by {filterKey}: <span className="font-semibold text-slate-900">{value}</span>
                </div>
              )}
            </div>

            {loading ? (
              <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                {[1, 2, 3, 4, 5, 6].map((item) => (
                  <div key={item} className="h-72 animate-pulse rounded-[2rem] bg-white shadow-sm" />
                ))}
              </div>
            ) : (
              <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/market-intelligence/${article.slug}`}
                    className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
                  >
                    <div className="aspect-[16/10] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700">
                      {article.featured_image ? (
                        <img
                          src={article.featured_image}
                          alt={article.title}
                          className="h-full w-full object-cover"
                          onError={handleMarketImageError}
                        />
                      ) : (
                        <div className="flex h-full items-end p-5 text-white">
                          <div>
                            <div className="text-xs uppercase tracking-[0.3em] text-[#f7911d]">
                              {article.category}
                            </div>
                            <div className="mt-2 text-2xl font-semibold">{clampText(article.title, 48)}</div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="space-y-3 p-5">
                      <div className="flex flex-wrap gap-2 text-xs font-medium text-slate-500">
                        <span className="rounded-full bg-slate-100 px-3 py-1">{article.content_type}</span>
                        <span>{formatMarketDate(article.published_at)}</span>
                      </div>
                      <div className="text-lg font-semibold text-slate-950">{article.title}</div>
                      <p className="text-sm leading-7 text-slate-600">
                        {article.excerpt || article.subtitle}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        )}

        {!loading && filteredArticles.length === 0 && (mode === 'archive' || mode === 'search' || mode === 'filter') && (
          <div className="mt-8 rounded-[2rem] border border-slate-200 bg-white p-8 text-center shadow-sm">
            <p className="text-slate-600">No insights found for this view yet.</p>
            <Link
              to="/market-intelligence"
              className="mt-4 inline-flex rounded-full bg-[#f7911d] px-6 py-3 text-sm font-semibold text-white"
            >
              Back to hub
            </Link>
          </div>
        )}
      </main>
    </div>
  );
};

export default MarketIntelligenceSection;
